import {
  Component,
  computed,
  DestroyRef,
  input,
  OnInit,
  Renderer2,
  Signal,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, EMPTY, finalize, of, take, tap } from 'rxjs';
import { ISidepanelTab } from 'src/app/components/sidepanel-tabs/sidepanel-tabs.component';
import { Label } from 'src/app/labels/englishLabels';
import { IntegrationModel, ResourceType } from 'src/app/models/api';
import {
  AgentClientApplicationIcon,
  AgentStepModel,
  AiService,
  AttachedDocument,
  StepType,
} from 'src/app/models/api/agentStep.model';
import { FindingModel } from 'src/app/models/api/finding.model';
import { JsonHalCollection } from 'src/app/models/api/jsonhal.model';
import { AiAgentStepActivityTableRowViewModel } from 'src/app/models/viewModels/aiAgentStepActivityTableRowViewModel';
import { DisplayCamelCaseStringPipe } from 'src/app/pipes/display-camel-case-string/display-camel-case-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import {
  AiAgentStepsHttpService,
  SensitiveDataRequestActions,
} from 'src/app/services/httpServices/detection/aiAgentStepsHttpService/ai-agent-steps-http.service';
import { AiFindingsHttpService } from 'src/app/services/httpServices/detection/aiFindingsHttpService/ai-findings-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { Router } from '@angular/router';
import { AgentStepsActionsService } from 'src/app/services/actions/agent-steps/agent-steps-actions.service';
import { AgentStepsSupportedActions } from 'src/app/services/actions/agent-steps/agent-steps-supported-actions';
import { MenuAction } from 'src/app/models/menuAction.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NotificationsService } from '../../../services/infra/notifications/notifications.service';
import { ICardField } from 'src/app/models/viewModels/cardModel';
import { getAiServiceLogoPath, getAiServiceDisplayName, isM365CustomAgent } from 'src/app/common/aiServiceUtils';
import { CitationResourceType, CitedSource } from './ai-agent-citation.component';

@Component({
  selector: 'app-agent-step',
  templateUrl: './enterprise-ai-agent-step.component.html',
  styleUrls: ['./enterprise-ai-agent-step.component.scss'],
})
export class AgentStepComponent implements OnInit {
  integration = input.required<string>();
  agentStep = input.required<string>();
  stepThreadId = input.required<string>();
  viewModel = input<AiAgentStepActivityTableRowViewModel | null>(null);

  public errorMessage = '';
  public isLoading$ = new BehaviorSubject<boolean>(true);
  public isThreadIdsLoading$ = new BehaviorSubject<boolean>(true);
  public step: AgentStepModel;
  public integrationName: string;
  public showNoData = false;
  public tabs: ISidepanelTab[];
  public activeTabIndex: WritableSignal<number> = signal(0);

  public attachments: WritableSignal<AttachedDocument[]> = signal([]);
  public attachmentTemplate = viewChild('attachmentTemplate', { read: TemplateRef });
  public attachmentActions: MenuAction[];
  public attachmentCardField: Signal<ICardField>;
  public customAgentTemplate = viewChild('customAgentTemplate', { read: TemplateRef });

  public stepDetailsCardFields: ICardField[];
  public threadCardFields: ICardField[] = [];
  public actorCardFieldList: ICardField[][];
  public clientCardFields: ICardField[];
  public findingsByThreadId: FindingModel[];

  public menuActions$ = new BehaviorSubject<MenuAction[]>([]);

  public isActionRunningSpinner = false;

  protected citedSources: WritableSignal<CitedSource[]> = signal([]);
  protected sourcesCardFieldList: WritableSignal<CitedSource[]> = signal([]);
  protected showMoreCitations: WritableSignal<boolean> = signal(false);
  protected showMoreSources: WritableSignal<boolean> = signal(false);

  protected citationAndSourceTabs: Signal<ISidepanelTab[]>;
  protected citationAndSourceActiveTab: WritableSignal<number> = signal(0);

  private downloadAttachmentAction: MenuAction = {
    name: 'download',
    displayNameLabel: 'commonActions.download.title',
    disabled: false,
  };
  private deleteAttachmentAction: MenuAction = {
    name: 'delete',
    displayNameLabel: 'commonActions.delete',
    disabled: false,
  };

  constructor(
    private _storeService: StoreService,
    private _aiAgentStepsService: AiAgentStepsHttpService,
    private _aiFindingsService: AiFindingsHttpService,
    private _httpErrorHandlerService: HttpErrorHandlerService,
    private _displayCamelCaseStringPipe: DisplayCamelCaseStringPipe,
    private _labelsProviderPipe: LabelsProviderPipe,
    private _router: Router,
    private _agentStepsActionsService: AgentStepsActionsService,
    private _notificationService: NotificationsService,
    private _destroyRef: DestroyRef,
    private _renderer: Renderer2
  ) {
    this.attachmentCardField = computed(() => ({
      key: this._labelsProviderPipe.transform('aiAgentStepPage.attachments'),
      customTemplate: this.attachmentTemplate(),
    }));

    this.citationAndSourceTabs = computed(() => {
      const tabs: ISidepanelTab[] = [];
      const citations = this.citedSources();
      if (citations.length) {
        tabs.push({ labelPath: 'aiAgentStepPage.citedSourcesCardTitle', value: `(${citations.length})` });
      }
      const sources = this.sourcesCardFieldList();
      if (sources.length) {
        tabs.push({ labelPath: 'aiAgentStepPage.sourcesCardTitle', value: `(${sources.length})` });
      }
      return tabs;
    });
  }

  public ngOnInit() {
    this._init(this.agentStep(), this.stepThreadId(), this.integration(), this.viewModel());
  }

  private _init(
    stepId: string,
    threadId: string,
    integrationId: string,
    stepView: AiAgentStepActivityTableRowViewModel | null
  ) {
    let step: AgentStepModel | null = null;
    if (stepView) {
      step = stepView.step;
    }
    const step$ = step ? of(step) : this._aiAgentStepsService.getAiAgentStep(integrationId, stepId);

    combineLatest([step$, this._storeService.integrations$])
      .pipe(
        take(1),
        finalize(() => this.isLoading$.next(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: async ([step, integrations]) => {
          this._handleDataResponse(step, integrations);
          const menuActions = this.viewModel()?.menuActions;
          if (menuActions) {
            this.menuActions$.next(menuActions);
          } else {
            const tempView: AiAgentStepActivityTableRowViewModel = {
              id: this.agentStep(),
              integrationId: this.integration(),
              actorName: '',
              timestamp: '',
              platformIcon: '',
              stepType: '',
              threadId: '',
              agentName: '',
              application: '',
              step: step,
              clientApplicationIcon: '',
              clientApplication: '',
              rowId: '',
              clickable: false,
            };
            await this._agentStepsActionsService.setActionsForAgentSteps([step], [tempView]);
            if (tempView.menuActions) this.menuActions$.next(tempView.menuActions);
          }
        },
        error: (error) => {
          if (error.status === 404) this.showNoData = true;
          else this._httpErrorHandlerService.handle(error);
        },
      });

    this._aiFindingsService
      .getFindingsByThreadId(threadId)
      .pipe(
        take(1),
        finalize(() => this.isThreadIdsLoading$.next(false)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (findings) => {
          this._handleThreadIdsResponse(findings);
        },
        error: (error) => {
          if (error.status !== 404) this._httpErrorHandlerService.handle(error);
        },
      });
  }

  public actionSelected(actionString: string) {
    const action = actionString as AgentStepsSupportedActions;
    const response = this._agentStepsActionsService.handleActionClicked(action, this.agentStep(), this.integration());
    response.subscribe({
      next: () => (this.isActionRunningSpinner = false),
    });
    switch (action) {
      case AgentStepsSupportedActions.chatGptEnterpriseDeleteConversationAction:
        this.isActionRunningSpinner = false;
        break;
    }
  }

  public performAttachmentAction($event: MenuAction, attachment: AttachedDocument) {
    switch ($event) {
      case this.downloadAttachmentAction:
        this._downloadAttachment(attachment);
        return;
      case this.deleteAttachmentAction:
        this._deleteAttachment(attachment);
        return;
    }
  }

  /**
   * Checks if the agent should be clickable (only for Copilot Studio agents or ChatGPT agents with valid ID)
   */
  public shouldShowAgentLink(aiService: AiService): boolean {
    if (aiService === AiService.copilotStudio) {
      return true;
    }

    if (aiService === AiService.chatGptEnterprise) {
      const resourceId = this.step?.botDetails?.platformInfo?.resourceId;
      return resourceId != null && resourceId.trim() !== '';
    }

    return false;
  }

  /**
   * Navigates to the agent in inventory
   */
  public navigateToAgent(): void {
    this._router.navigateByUrl(
      `app/inventory?q=type:${ResourceType.copilot} search:"${this.step.botDetails.platformInfo.resourceId}"`
    );
  }

  public getAiServiceIconPath(): string {
    if (!this.step) return '';
    return getAiServiceLogoPath(this.step.service);
  }

  public getAgentHeaderDisplayName(): string {
    if (!this.step) {
      return '';
    }
    try {
      return getAiServiceDisplayName(this.step.service);
    } catch {
      return '';
    }
  }

  private async _handleThreadIdsResponse(findings: JsonHalCollection<FindingModel>): Promise<void> {
    this.findingsByThreadId = findings._embedded.items;
    const relatedFindings = this._getSeverityCount(this.findingsByThreadId);
    this.threadCardFields = [
      {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.threadIdFieldName'),
        value: this.stepThreadId(),
        copyableText: this.stepThreadId(),
        isCopyable: true,
      },
      {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.findingsFieldName'),
        dictSeverity: relatedFindings,
        value: this._labelsProviderPipe.transform('aiAgentStepPage.noFindingsFound'),
      },
      {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.environmentNameFieldName'),
        value: this.step?.environment?.displayName ?? '',
        copyableText: this.step?.environment?.displayName ?? '',
        isCopyable: true,
      },
      ...this.threadCardFields,
    ];
  }

  private async _handleDataResponse(step: AgentStepModel, integrations: IntegrationModel[]): Promise<void> {
    const selectedIntegration: IntegrationModel | undefined = integrations.find(
      (integration) => integration.id === this.integration()
    );
    this.integrationName = selectedIntegration?.name ?? '';

    this.step = step;
    this.showNoData = false;
    this.tabs = [{ labelPath: 'inventoryResourcePage.overviewTabTitle' }];
    const isCustomM365Agent = isM365CustomAgent(this.step.service, this.step.botDetails.displayName);
    const agentField: ICardField = {
      key: this._labelsProviderPipe.transform('aiAgentStepPage.agentDetailsFieldName'),
    };

    // Use custom template for custom M365 agents, regular display for others
    if (isCustomM365Agent) {
      agentField.customTemplate = this.customAgentTemplate();
    } else {
      agentField.value = this._displayCamelCaseStringPipe.transform(this.step.botDetails.displayName);
      // Only add navigation method for Copilot Studio agents or ChatGPT agents with valid ID
      if (
        this.step.service === AiService.copilotStudio ||
        (this.step.service === AiService.chatGptEnterprise &&
          this.step.botDetails?.platformInfo?.resourceId &&
          this.step.botDetails.platformInfo.resourceId.trim() !== '')
      ) {
        agentField.method = () => {
          this._router.navigateByUrl(
            `app/inventory?q=type:${ResourceType.copilot} search:"${step.botDetails.platformInfo.resourceId}"`
          );
        };
      }
    }

    this.threadCardFields.push(agentField);

    this._addStepDetailsCardFields();

    this.actorCardFieldList = this.step.relatedActors.map((actor) => {
      const fields: ICardField[] = [];
      fields.push({
        key: this._labelsProviderPipe.transform('aiAgentStepPage.nameFieldName'),
        value: actor.displayName,
      });
      if (actor.email)
        fields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.emailFieldName'),
          value: actor.email,
        });
      return fields;
    });

    this.clientCardFields = [
      {
        key: this._labelsProviderPipe.transform('aiAgentStepPage.applicationFieldName'),
        value: this._displayCamelCaseStringPipe.transform(this.step.clientContext.clientApplication),
        iconPath: Object.values(AgentClientApplicationIcon).includes(
          this.step.clientContext.clientApplication.toString() as AgentClientApplicationIcon
        )
          ? `assets/images/logo/agent-client-app-${this.step.clientContext.clientApplication}.svg`
          : 'assets/images/logo/agent-client-app-other.svg',
      },
    ];
    if (this.step.clientContext.region) {
      this.clientCardFields.push({
        key: this._labelsProviderPipe.transform('aiAgentStepPage.regionFieldName'),
        value: this.step.clientContext.region,
      });
    }
    if (this.step.clientContext.ipAddress) {
      this.clientCardFields.push({
        key: this._labelsProviderPipe.transform('aiAgentStepPage.ipAddressFieldName'),
        value: this.step.clientContext.ipAddress,
      });
    }
  }

  private _getSeverityCount(findings: FindingModel[]): Record<string, number> {
    return findings.reduce(
      (acc, finding) => {
        const severity = finding.rule.severity;
        acc[severity] = (acc[severity] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }

  private _addStepDetailsCardFields() {
    this.stepDetailsCardFields = [];
    const citedSources: CitedSource[] = [];
    switch (this.step.type) {
      case StepType.aiMessageStep:
      case StepType.userMessageStep:
        this.stepDetailsCardFields.push({
          key: this._labelsProviderPipe.transform('aiAgentStepPage.messageFieldName'),
          value: '',
          showPopupBtn: true,
          sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.showPromptContentLinkName'),
          isRawValue: true,
          sensitiveDataMethod: () =>
            this._aiAgentStepsService.getAgentStepSensitiveData(
              this.integration(),
              this.agentStep(),
              SensitiveDataRequestActions.transcript
            ),
        });
        if (this.step.service === AiService.chatGptEnterprise) {
          this.attachmentActions = [this.downloadAttachmentAction, this.deleteAttachmentAction];
        } else {
          this.attachmentActions = [];
        }
        if (this.step.isTranscriptFetchingAllowed != null) {
          this.stepDetailsCardFields.push({
            key: this._labelsProviderPipe.transform('aiAgentStepPage.transcriptFetchingAllowedFieldName'),
            value: this.step.isTranscriptFetchingAllowed ? 'Yes' : 'No',
            tooltipLabelPath: 'aiAgentStepPage.transcriptFetchingAllowedFieldDescription',
            tooltipIconPath: 'assets/images/common/info.svg',
          });
        }
        if ('attachedDocuments' in this.step && this.step.attachedDocuments?.length) {
          this.attachments.set(this.step.attachedDocuments);
          this.stepDetailsCardFields.push(this.attachmentCardField());
        }

        break;
      case StepType.ragStep:
        switch (this.step.service) {
          case AiService.m365Copilot:
            for (const source of this.step.citedSources) {
              citedSources.push({
                id: source.id,
                fileName: source.name,
                link: source.url ?? '',
                sensitivityLabelId: source.sensitivityLabelId,
                resourceType:
                  source.m365ResourceType === 'website' ? CitationResourceType.WEBSITE : CitationResourceType.FILE,
              });
            }
            break;
          case AiService.copilotStudio:
            if (this.step.ragQuery)
              this.stepDetailsCardFields.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
                value: this.step.ragQuery,
                sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.ragQuery'),
                isRawValue: true,
                showPopupBtn: false,
              });
            if (this.step.sensitivityLabelId)
              this.stepDetailsCardFields.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.sensitivityLabelFieldName'),
                value: this.step.sensitivityLabelId,
                isCopyable: true,
                copyableText: this.step.sensitivityLabelId,
              });
            for (const source of this.step.citedSources) {
              citedSources.push({
                id: source.id,
                fileName: source.name,
                link: source.url ?? '',
                sensitivityLabelId: source.sensitivityLabelId,
                resourceType: CitationResourceType.FILE,
                sensitiveData: {
                  buttonText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchCitationSnippet'),
                  isRawValue: true,
                  showPopupBtn: true,
                  sensitiveDataMethod: () =>
                    this._aiAgentStepsService.getAgentStepSensitiveData(
                      this.integration(),
                      this.agentStep(),
                      SensitiveDataRequestActions.snippet,
                      source.id
                    ),
                },
              });
            }
            if (this.step.sources) {
              for (const source of this.step.sources) {
                this.sourcesCardFieldList.update((sources) => [
                  ...sources,
                  {
                    id: source.id,
                    fileName: source.name ?? source.id,
                    resourceType: CitationResourceType.FILE,
                    link: source.url ?? '',
                    sensitivityLabelId: source.sensitivityLabelId,
                  },
                ]);
              }
            }
            break;
          case AiService.chatGptEnterprise:
          case AiService.azureAiFoundry:
            for (const source of this.step.citedSources) {
              citedSources.push({
                id: source.id,
                fileName: source.name,
                link: source.url ?? '',
                resourceType: CitationResourceType.FILE,
              });
            }
            break;
        }
        break;
      case StepType.triggerStep:
        switch (this.step.service) {
          case AiService.copilotStudio:
            this.stepDetailsCardFields.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.fetchPayload'),
              value: '',
              confirmedData: {
                title: Label.get('aiAgentStepPayloadDialog.confirmationDialogTitle'),
                content: Label.get('aiAgentStepPayloadDialog.confirmationDialogDescription'),
                positiveOptionText: Label.get('aiAgentStepPayloadDialog.confirmationDialogYes'),
                negativeOptionText: Label.get('aiAgentStepPayloadDialog.confirmationDialogNo'),
              },
              isRawValue: true,
              sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchPayload'),
              showPopupBtn: true,
              sensitiveDataMethod: () =>
                this._aiAgentStepsService.getAgentStepSensitiveData(
                  this.integration(),
                  this.agentStep(),
                  SensitiveDataRequestActions.payload
                ),
            });
            if (this.step.triggerDisplayName)
              this.stepDetailsCardFields.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.triggeringEvent'),
                value: this.step.triggerDisplayName,
                iconPath: this.step.triggerIconUrl,
              });
            break;
        }
        break;
      case StepType.toolInvocationStep:
        switch (this.step.service) {
          case AiService.copilotStudio:
            this.stepDetailsCardFields.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
              value: '',
              confirmedData: {
                title: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogTitle'),
                content: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogDescription'),
                positiveOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogYes'),
                negativeOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogNo'),
              },
              isRawValue: true,
              sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchToolInvocationParams'),
              showPopupBtn: true,
              sensitiveDataMethod: () =>
                this._aiAgentStepsService.getAgentStepSensitiveData(
                  this.integration(),
                  this.agentStep(),
                  SensitiveDataRequestActions.invocationParams
                ),
            });
            if (this.step.toolInvocations.length > 0 && this.step.toolInvocations[0].toolDisplayName)
              this.stepDetailsCardFields.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
                value: this.step.toolInvocations[0].toolDisplayName,
                iconPath: this.step.triggerIconUrl,
              });
            this.stepDetailsCardFields.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
              value: '',
              confirmedData: {
                title: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogTitle'),
                content: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogDescription'),
                positiveOptionText: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogYes'),
                negativeOptionText: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogNo'),
              },
              isRawValue: true,
              sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchToolInvocationResult'),
              showPopupBtn: true,
              sensitiveDataMethod: () =>
                this._aiAgentStepsService.getAgentStepSensitiveData(
                  this.integration(),
                  this.agentStep(),
                  SensitiveDataRequestActions.invocationResult
                ),
            });
            break;
          case AiService.chatGptEnterprise:
          case AiService.azureAiFoundry:
          case AiService.vertexAi:
            this.stepDetailsCardFields.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.invocationParameters'),
              value: '',
              confirmedData: {
                title: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogTitle'),
                content: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogDescription'),
                positiveOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogYes'),
                negativeOptionText: Label.get('aiAgentStepToolInvocationParamsDialog.confirmationDialogNo'),
              },
              isRawValue: true,
              sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchToolInvocationParams'),
              showPopupBtn: true,
              sensitiveDataMethod: () =>
                this._aiAgentStepsService.getAgentStepSensitiveData(
                  this.integration(),
                  this.agentStep(),
                  SensitiveDataRequestActions.invocationParams
                ),
            });
            if (this.step.toolInvocations.length > 0 && this.step.toolInvocations[0].toolDisplayName)
              this.stepDetailsCardFields.push({
                key: this._labelsProviderPipe.transform('aiAgentStepPage.toolField'),
                value: this.step.toolInvocations[0].toolDisplayName,
              });
            this.stepDetailsCardFields.push({
              key: this._labelsProviderPipe.transform('aiAgentStepPage.toolInvocationResult'),
              value: '',
              confirmedData: {
                title: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogTitle'),
                content: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogDescription'),
                positiveOptionText: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogYes'),
                negativeOptionText: Label.get('aiAgentStepToolInvocationResultDialog.confirmationDialogNo'),
              },
              isRawValue: true,
              sensitiveBtnText: this._labelsProviderPipe.transform('aiAgentStepPage.fetchToolInvocationResult'),
              showPopupBtn: true,
              sensitiveDataMethod: () =>
                this._aiAgentStepsService.getAgentStepSensitiveData(
                  this.integration(),
                  this.agentStep(),
                  SensitiveDataRequestActions.invocationResult
                ),
            });
            break;
        }
        break;
    }

    this.citedSources.set(citedSources);
  }

  private _deleteAttachment(deletedAttachment: AttachedDocument) {
    this._aiAgentStepsService
      .deleteAiAgentStepAttachment(deletedAttachment.id, this.integration(), this.agentStep())
      .pipe(
        tap(() => {
          this.attachments.update((attachments) =>
            attachments.filter((attachment) => attachment !== deletedAttachment)
          );
          if (!this.attachments().length) {
            this.stepDetailsCardFields = this.stepDetailsCardFields.filter(
              (field) => field !== this.attachmentCardField()
            );
          }
          this._notificationService.success(Label.get('aiAgentStepPage.attachmentDeletedSuccessfully'));
        }),
        catchError(() => {
          this._notificationService.error(Label.get('aiAgentStepPage.attachmentDeletionFailed'));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  private _downloadAttachment(attachment: AttachedDocument) {
    this._aiAgentStepsService
      .downloadAiAgentStepAttachment(attachment.id, attachment.displayName, this.integration(), this.agentStep())
      .pipe(
        tap((response) => {
          const anchor: HTMLAnchorElement = this._renderer.createElement('a');
          anchor.href = response.fileUrl;
          anchor.download = attachment.displayName;
          anchor.target = '_blank';
          anchor.click();
          this._notificationService.success(Label.get('aiAgentStepPage.attachmentDownloadedSuccessfully'));
        }),
        catchError(() => {
          this._notificationService.error(Label.get('aiAgentStepPage.attachmentDownloadFailed'));
          return EMPTY;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }
}
